import { useCallback, useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Feed } from '@factorialco/f0-react/icons/app'
import {
  MyTrainingsDataCollectionDocument,
  MyTrainingsDataCollectionFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useTrainingsDatatable } from 'modules/trainings/hooks/useTrainingsDatatable'
import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'

import emptyStateImage from './Empty-state-catalog-trainings-card.png'

export const MyTrainingCardsView = () => {
  const { navigation } = useNavigation()
  const { access } = useCurrentSession()

  const [myCourses, setMyCourses] = useState<MyTrainingsDataCollectionFragment[]>([])
  const { getDataCollectionTrainingAttendanceColor, getTrainingAttendanceTranslation } =
    useTrainingsDatatable()

  const getMandatoryCount = useCallback(
    (groupId: boolean | undefined) => {
      return myCourses.length
        ? myCourses.filter((node) =>
            String(groupId) === 'true' ? node.isMandatory : !node.isMandatory
          ).length
        : 0
    },
    [myCourses]
  )

  const source = useGraphqlDataCollectionSource({
    document: MyTrainingsDataCollectionDocument,
    mapData: (data) => {
      const nodes = data?.trainings?.trainingsConnection?.nodes ?? []
      setMyCourses(nodes)
      return nodes
    },
    mapPagination: (data) => {
      return {
        total: data?.trainings?.trainingsConnection?.totalCount ?? 0,
      }
    },
    mapVariables: ({ filters, search, sortings }) => {
      const filterObject: Record<string, unknown> = {}

      if (filters.status && filters.status.length > 0) {
        filterObject.trainingAttendanceStatus = { in: filters.status }
      }

      if (filters.creationYear && filters.creationYear.length > 0) {
        const year = Number(filters.creationYear[0])
        if (!isNaN(year)) {
          const yearStart = `${year}-01-01`
          const yearEnd = `${year}-12-31`
          filterObject.createdAt = { between: [yearStart, yearEnd] }
        }
      }
      return {
        accessId: access?.id,
        search,
        filter: Object.keys(filterObject).length > 0 ? filterObject : undefined,
        status: 'active',
        sortOrder: sortings?.[0],
      }
    },
    filters: {
      status: {
        label: i18n.t('trainings.my_trainings_revamp.overview.courses_datatable.status_column'),
        type: 'in',
        options: {
          cache: true,
          options: async () => [
            {
              label: i18n.t('trainings.my_training.all_courses_table.not_started'),
              value: 'notstarted',
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.started'),
              value: 'started',
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.completed'),
              value: 'completed',
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.partially_completed'),
              value: 'partiallycompleted',
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.absent'),
              value: 'missing',
            },
          ],
        },
      },
      creationYear: {
        label: i18n.t('trainings.create_training_modal.year'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const currentYear = new Date().getFullYear()
            const years = []
            for (let i = currentYear + 1; i >= currentYear - 25; i--) {
              years.push({
                label: String(i),
                value: String(i),
              })
            }
            return years
          },
        },
      },
    },
    search: {
      enabled: true,
    },
    sortings: {
      isMandatory: {
        label: i18n.t(
          'trainings.my_trainings_revamp.overview.courses_datatable.group_by.mandatory'
        ),
      },
    },
    defaultSortings: {
      field: 'isMandatory',
      order: 'asc',
    },
    itemUrl: (item: MyTrainingsDataCollectionFragment) => {
      return navigation.myTrainings.revamp.detailsRevamp.view.overview.fullPath({
        trainingId: item.id,
      })
    },

    grouping: {
      collapsible: true,
      defaultOpenGroups: true,
      mandatory: true,
      groupBy: {
        isMandatory: {
          name: i18n.t(
            'trainings.my_trainings_revamp.overview.courses_datatable.group_by.mandatory'
          ),
          defaultDirection: 'asc' as const,
          label(groupId) {
            return String(groupId) === 'true'
              ? i18n.t(
                  'trainings.my_trainings_revamp.overview.courses_datatable.group_by.mandatory_learning'
                )
              : i18n.t(
                  'trainings.my_trainings_revamp.overview.courses_datatable.group_by.optional_learning'
                )
          },
          itemCount: (groupId) => getMandatoryCount(groupId),
        },
      },
    },
  })

  return (
    <OneDataCollection
      id="trainings/my_trainings_cards_view/v1"
      storage={DEFAULT_DATA_COLLECTION_STORAGE}
      source={source}
      visualizations={[
        {
          type: 'card',
          options: {
            title: (item) => item.name,
            image: (item) => item.thumbnail ?? emptyStateImage,
            cardProperties: [
              {
                label: '',
                icon: Feed,
                render: (item) => ({
                  type: 'status',
                  value: {
                    status: getDataCollectionTrainingAttendanceColor(
                      item?.trainingAttendanceStatus
                    ),
                    label: item.trainingAttendanceStatus
                      ? getTrainingAttendanceTranslation(item?.trainingAttendanceStatus)
                      : '',
                  },
                }),
              },
            ],
          },
        },
      ]}
    />
  )
}
